import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { CssBaseline } from '@mui/material';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';

import { createAppTheme } from './theme';

export const THEME_STORAGE_KEY = 'hiring-motion-theme';
export const THEMES = {
  LIGHT: 'light',
  DARK: 'dark',
};

const ThemeModeContext = createContext({
  mode: THEMES.DARK,
  isDarkMode: true,
  toggleTheme: () => {},
  setThemeMode: () => {},
});

const getInitialTheme = () => {
  if (typeof window === 'undefined') return THEMES.DARK;

  const storedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (storedTheme === THEMES.LIGHT || storedTheme === THEMES.DARK) return storedTheme;

  return THEMES.DARK;
};

export function ThemeModeProvider({ children }) {
  const [mode, setMode] = useState(getInitialTheme);

  useEffect(() => {
    document.documentElement.dataset.theme = mode;
    document.documentElement.style.colorScheme = mode;
    window.localStorage.setItem(THEME_STORAGE_KEY, mode);
  }, [mode]);

  const value = useMemo(
    () => ({
      mode,
      isDarkMode: mode === THEMES.DARK,
      toggleTheme: () => {
        if (!document.startViewTransition) {
          setMode((currentMode) => (currentMode === THEMES.DARK ? THEMES.LIGHT : THEMES.DARK));
          return;
        }
        document.startViewTransition(() => {
          flushSync(() => {
            setMode((currentMode) => (currentMode === THEMES.DARK ? THEMES.LIGHT : THEMES.DARK));
          });
        });
      },
      setThemeMode: (nextMode) => {
        if (nextMode === THEMES.LIGHT || nextMode === THEMES.DARK) setMode(nextMode);
      },
    }),
    [mode]
  );

  const theme = useMemo(() => createAppTheme(mode), [mode]);

  return (
    <ThemeModeContext.Provider value={value}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          {children}
        </ThemeProvider>
      </StyledEngineProvider>
    </ThemeModeContext.Provider>
  );
}

export const useThemeMode = () => useContext(ThemeModeContext);
